'use client'
import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import { Box, Paper } from '@material-ui/core'
import { SeatStatus, PositionDescriptor } from 'config/constants'
import { getDescriptor } from 'views/utils/functions'
import Seat from '../components/Seat'

const SeatPlanner = forwardRef(
  (
    { seatGrid, rows, columns, columnDescriptor, rowDescriptor, onSeatClick },
    ref
  ) => {
    const containerRef = React.useRef(null)
    const isDraggingRef = useRef(false)

    const handleSeatMouseDown = useCallback(
      (row, column) => {
        isDraggingRef.current = true
        onSeatClick(row, column)
      },
      [onSeatClick]
    )

    const handleSeatMouseEnter = useCallback(
      (row, column) => {
        if (isDraggingRef.current) {
          onSeatClick(row, column)
        }
      },
      [onSeatClick]
    )

    useEffect(() => {
      const handleMouseUp = () => {
        isDraggingRef.current = false
      }
      window.addEventListener('mouseup', handleMouseUp)
      return () => window.removeEventListener('mouseup', handleMouseUp)
    }, [])

    return (
      <Paper
        elevation={0}
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '-webkit-fill-available',
          p: 2,
          mt: 3,
          backgroundColor: '#f5f5f5'
        }}>
        <Box
          ref={containerRef}
          sx={{
            display: 'grid',
            gap: 1,
            maxWidth: 1200,
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingRight: '4px',
            userSelect: 'none'
          }}>
          <Box
            sx={{
              display: 'flex',
              mb: 1,
              ml: rowDescriptor === PositionDescriptor.Roman ? '55px' : '36px',
              paddingTop:
                columnDescriptor === PositionDescriptor.Roman ? '20px' : '',
              paddingBottom:
                columnDescriptor === PositionDescriptor.Roman ? '2px' : '',
              gap:
                columnDescriptor === PositionDescriptor.Roman
                  ? '1.6px'
                  : '1.5px'
            }}>
            {Array.from({ length: columns }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: 30,
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  transform:
                    columnDescriptor === PositionDescriptor.Roman
                      ? 'rotate(-45deg)'
                      : 'none'
                }}>
                {getDescriptor(columnDescriptor, i + 1)}
              </Box>
            ))}
          </Box>

          {Array.from({ length: rows }, (_, rowIndex) => (
            <Box key={rowIndex} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 30,
                  mr: rowDescriptor === PositionDescriptor.Roman ? 3.2 : 1,
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}>
                {getDescriptor(rowDescriptor, rowIndex + 1)}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {Array.from({ length: columns }, (_, colIndex) => {
                  const seat = seatGrid[rowIndex]?.[colIndex] ?? {
                    row: rowIndex + 1,
                    column: colIndex + 1,
                    status: SeatStatus.Empty
                  }
                  return (
                    <Seat
                      key={`${rowIndex}-${colIndex}`}
                      row={seat.row}
                      rowDescriptor={rowDescriptor}
                      column={seat.column}
                      columnDescriptor={columnDescriptor}
                      status={seat.status}
                      onClick={onSeatClick}
                      onMouseDown={handleSeatMouseDown}
                      onMouseEnter={handleSeatMouseEnter}
                    />
                  )
                })}
              </Box>
              <Box
                sx={{
                  width: 30,
                  ml: rowDescriptor === PositionDescriptor.Roman ? 3.2 : 1,
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}>
                {getDescriptor(rowDescriptor, rowIndex + 1)}
              </Box>
            </Box>
          ))}
          <Box
            sx={{
              display: 'flex',
              mt: 1,
              ml: rowDescriptor === PositionDescriptor.Roman ? '55px' : '36px',
              paddingTop:
                columnDescriptor === PositionDescriptor.Roman ? '20px' : '',
              paddingBottom:
                columnDescriptor === PositionDescriptor.Roman ? '2px' : '',
              gap:
                columnDescriptor === PositionDescriptor.Roman
                  ? '1.6px'
                  : '1.5px'
            }}>
            {Array.from({ length: columns }, (_, i) => (
              <Box
                key={i}
                sx={{
                  width: 30,
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  transform:
                    columnDescriptor === PositionDescriptor.Roman
                      ? 'rotate(-45deg)'
                      : 'none'
                }}>
                {getDescriptor(columnDescriptor, i + 1)}
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    )
  }
)

export default React.memo(
  SeatPlanner,
  (prev, next) =>
    prev.seatGrid === next.seatGrid &&
    prev.rows === next.rows &&
    prev.columns === next.columns
)
